import React, { useState, useEffect, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { notificacionAPI, authAPI, moduloAPI, empresaAPI } from '../lib/api';
import { cn, getRolLabel } from '../lib/utils';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import PWAInstallBanner from './PWAInstallBanner';
import ThemeToggle from './ThemeToggle';
import { useGuiasPushSubscription } from '../hooks/useGuiasPushSubscription';
import { toast } from 'sonner';
import {
    FileText,
    Home,
    Users,
    Settings,
    Bell,
    LogOut,
    Menu,
    Shield,
    ClipboardCheck,
    Upload,
    History,
    Building2,
    User,
    X,
    BarChart3,
    Blocks,
    KeyRound,
    Eye,
    EyeOff,
    Loader2,
    Plane,
    ChevronDown,
    ChevronRight,
    Star,
    Package,
    Wrench,
    Satellite,
    ListChecks,
} from 'lucide-react';

// ==================== CHANGE PASSWORD MODAL ====================
const ChangePasswordModal = ({ open, onClose }) => {
    const [currentPw, setCurrentPw] = useState('');
    const [newPw, setNewPw] = useState('');
    const [confirmPw, setConfirmPw] = useState('');
    const [showCurrent, setShowCurrent] = useState(false);
    const [showNew, setShowNew] = useState(false);
    const [saving, setSaving] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (newPw.length < 6) return toast.error('La nueva contraseña debe tener al menos 6 caracteres');
        if (newPw !== confirmPw) return toast.error('Las contraseñas no coinciden');
        setSaving(true);
        try {
            await authAPI.changeOwnPassword(currentPw, newPw);
            toast.success('Contraseña actualizada correctamente');
            onClose();
            setCurrentPw(''); setNewPw(''); setConfirmPw('');
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Error al cambiar contraseña');
        } finally {
            setSaving(false);
        }
    };

    if (!open) return null;

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50" onClick={onClose}>
            <div className="bg-card border rounded-xl shadow-xl w-full max-w-md mx-4 p-6" onClick={e => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-4">
                    <h3 className="font-heading font-bold text-lg flex items-center gap-2">
                        <KeyRound className="h-5 w-5 text-primary" /> Cambiar Contraseña
                    </h3>
                    <Button variant="ghost" size="icon" onClick={onClose}><X className="h-4 w-4" /></Button>
                </div>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="space-y-1">
                        <Label>Contraseña actual</Label>
                        <div className="relative">
                            <Input type={showCurrent ? 'text' : 'password'} value={currentPw} onChange={e => setCurrentPw(e.target.value)} required data-testid="current-password-input" />
                            <button type="button" className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" onClick={() => setShowCurrent(!showCurrent)}>
                                {showCurrent ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                            </button>
                        </div>
                    </div>
                    <div className="space-y-1">
                        <Label>Nueva contraseña</Label>
                        <div className="relative">
                            <Input type={showNew ? 'text' : 'password'} value={newPw} onChange={e => setNewPw(e.target.value)} required minLength={6} data-testid="new-password-input" />
                            <button type="button" className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" onClick={() => setShowNew(!showNew)}>
                                {showNew ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                            </button>
                        </div>
                    </div>
                    <div className="space-y-1">
                        <Label>Confirmar nueva contraseña</Label>
                        <Input type={showNew ? 'text' : 'password'} value={confirmPw} onChange={e => setConfirmPw(e.target.value)} required data-testid="confirm-password-input" />
                    </div>
                    <Button type="submit" className="w-full" disabled={saving} data-testid="change-password-submit">
                        {saving ? <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Guardando...</> : 'Cambiar Contraseña'}
                    </Button>
                </form>
            </div>
        </div>
    );
};

// ==================== MODULE ICON MAP ====================
const moduleIconMap = {
    FileText, Package, BarChart3, Users, Settings, ClipboardCheck,
    Shield, Satellite, Plane, Wrench, Bell, Blocks, Star, ListChecks,
};

// ==================== MODULE NAV DEFINITIONS ====================
// Keyed by module code -> role -> nav items
const moduleNavConfig = {
    guias_despacho: {
        admin: [
            { href: '/guias/dashboard', label: 'Dashboard', icon: Home },
            { href: '/guias/todos', label: 'Guías', icon: FileText },
            { href: '/guias/reportes', label: 'Reportes', icon: BarChart3 },
            { href: '/guias/usuarios', label: 'Usuarios', icon: Users },
            { href: '/guias/carga-masiva', label: 'Carga Masiva', icon: Upload },
            { href: '/guias/historial', label: 'Historial', icon: History },
            { href: '/guias/configuracion', label: 'Configuración', icon: Settings },
        ],
        solicitante: [
            { href: '/guias/dashboard', label: 'Dashboard', icon: Home },
            { href: '/guias/crear', label: 'Nueva Guía', icon: FileText },
            { href: '/guias/mis-guias', label: 'Mis Guías', icon: ClipboardCheck },
        ],
        gerente: [
            { href: '/guias/dashboard', label: 'Dashboard', icon: Home },
            { href: '/guias/aprobar', label: 'Aprobar Guías', icon: ClipboardCheck },
            { href: '/guias/todos', label: 'Todas las Guías', icon: FileText },
            { href: '/guias/reportes', label: 'Reportes', icon: BarChart3 },
            { href: '/guias/historial', label: 'Historial', icon: History },
        ],
        seguridad: [
            { href: '/guias/todos', label: 'Todas las Guías', icon: FileText },
            { href: '/guias/punto-salida', label: 'Punto de Salida', icon: Shield },
            { href: '/guias/reportes', label: 'Reportes', icon: BarChart3 },
        ],
        superadmin: [
            { href: '/guias/dashboard', label: 'Dashboard', icon: Home },
            { href: '/guias/todos', label: 'Guías', icon: FileText },
            { href: '/guias/reportes', label: 'Reportes', icon: BarChart3 },
            { href: '/guias/aprobar', label: 'Aprobar', icon: ClipboardCheck },
            { href: '/guias/historial', label: 'Historial', icon: History },
            { href: '/guias/configuracion', label: 'Configuración', icon: Settings },
        ],
        tecnico: [
            { href: '/guias/dashboard', label: 'Dashboard', icon: Home },
            { href: '/guias/todos', label: 'Guías', icon: FileText },
        ],
    },
    solicitudes_permisos: {
        admin: [
            { href: '/solicitudes/dashboard', label: 'Dashboard', icon: Home },
            { href: '/solicitudes/lista', label: 'Solicitudes', icon: ListChecks },
            { href: '/solicitudes/admin/tipos', label: 'Tipos', icon: Settings },
        ],
        seguridad: [
            { href: '/solicitudes/dashboard', label: 'Dashboard', icon: Home },
            { href: '/solicitudes/lista', label: 'Solicitudes', icon: ListChecks },
        ],
        superadmin: [
            { href: '/solicitudes/dashboard', label: 'Dashboard', icon: Home },
            { href: '/solicitudes/lista', label: 'Solicitudes', icon: ListChecks },
            { href: '/solicitudes/admin/tipos', label: 'Tipos', icon: Settings },
        ],
    },
};

// Route prefix → module code mapping
const routePrefixToModule = {
    '/guias': 'guias_despacho',
    '/solicitudes': 'solicitudes_permisos',
    '/ordenes': 'ordenes_servicio',
    '/alarmas': 'alarmas_ls',
    '/codelco': 'integrador_codelco',
};

// ==================== NAV LINK ====================
const NavLink = ({ item, isActive, onClick }) => {
    const Icon = item.icon;
    return (
        <Link
            to={item.href}
            onClick={onClick}
            className={cn(
                'flex items-center gap-3 px-3 py-2 rounded-sm text-sm font-medium transition-colors',
                'hover:bg-primary/10 hover:text-primary',
                isActive && 'bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground',
                !isActive && 'text-muted-foreground'
            )}
            data-testid={`nav-${item.href.replace(/\//g, '-').replace(/^-/, '')}`}
        >
            <Icon className="h-4 w-4 shrink-0" />
            <span className="uppercase text-xs tracking-wider">{item.label}</span>
        </Link>
    );
};

// ==================== MODULE SECTION ====================
const ModuleSection = ({ modulo, items, expanded, onToggle, isPrincipal, onSetPrincipal, currentPath, onNavClick }) => {
    const Icon = moduleIconMap[modulo.icono] || Package;
    const isCurrentModule = Object.entries(routePrefixToModule).some(
        ([prefix, code]) => currentPath.startsWith(prefix) && code === modulo.codigo
    );

    const isOpen = expanded || isCurrentModule;

    return (
        <div className="mb-1" data-testid={`module-section-${modulo.codigo}`}>
            <button
                onClick={onToggle}
                className={cn(
                    'w-full flex items-center gap-2 px-3 py-2.5 rounded-sm transition-colors text-left group',
                    isOpen ? 'bg-primary/5 text-foreground' : 'text-muted-foreground hover:bg-muted/50 hover:text-foreground'
                )}
                data-testid={`module-toggle-${modulo.codigo}`}
            >
                <Icon className={cn('h-4 w-4 shrink-0', isOpen && 'text-primary')} />
                <span className="flex-1 text-xs font-bold uppercase tracking-wider truncate">{modulo.nombre}</span>
                <button
                    onClick={(e) => { e.stopPropagation(); onSetPrincipal(); }}
                    className={cn(
                        'p-0.5 rounded-sm transition-all',
                        isPrincipal
                            ? 'text-amber-500'
                            : 'text-transparent group-hover:text-muted-foreground/40 hover:!text-amber-500'
                    )}
                    title={isPrincipal ? 'Módulo principal' : 'Fijar como principal'}
                    data-testid={`module-star-${modulo.codigo}`}
                >
                    <Star className={cn('h-3.5 w-3.5', isPrincipal && 'fill-amber-500')} />
                </button>
                {isOpen ? <ChevronDown className="h-3.5 w-3.5 shrink-0" /> : <ChevronRight className="h-3.5 w-3.5 shrink-0" />}
            </button>
            {isOpen && (
                <div className="ml-2 pl-2 border-l border-border/50 mt-1 space-y-0.5">
                    {items.map((item) => (
                        <NavLink
                            key={item.href}
                            item={item}
                            isActive={currentPath === item.href}
                            onClick={onNavClick}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

// ==================== BRAND BLOCK ====================
const BrandBlock = ({ empresaNombre, logoUrl, onClick, size = 'lg', brandConfig }) => {
    const showTitle = brandConfig?.brand_show_title !== false;
    const showNombre = brandConfig?.brand_show_nombre !== false;
    const logoHeight = brandConfig?.brand_logo_height || 80;
    const titleSize = brandConfig?.brand_title_size || 14;
    const nombreSize = brandConfig?.brand_nombre_size || 12;
    const logoTop = brandConfig?.brand_logo_top || 0;
    const areaHeight = brandConfig?.brand_area_height || 0;
    const scale = size === 'lg' ? 1 : 0.7;

    return (
        <div
            className="cursor-pointer flex flex-col"
            onClick={onClick}
            style={areaHeight > 0 ? { minHeight: `${Math.round(areaHeight * scale)}px` } : undefined}
        >
            {logoUrl && (
                <img
                    src={logoUrl}
                    alt="Logo"
                    className="object-contain max-w-[200px]"
                    style={{
                        height: `${Math.round(logoHeight * scale)}px`,
                        marginTop: logoTop > 0 ? `${Math.round(logoTop * scale)}px` : undefined,
                        marginBottom: '8px',
                    }}
                    onError={(e) => { e.target.style.display = 'none'; }}
                />
            )}
            {showTitle && (
                <h2
                    className="font-heading font-black uppercase tracking-tight text-primary whitespace-nowrap"
                    style={{ fontSize: `${Math.round(titleSize * scale)}px` }}
                >
                    PROTECCIÓN INDUSTRIAL
                </h2>
            )}
            {showNombre && empresaNombre && (
                <p
                    className="text-muted-foreground uppercase tracking-wider mt-0.5"
                    style={{ fontSize: `${Math.round(nombreSize * scale)}px` }}
                >
                    {empresaNombre}
                </p>
            )}
        </div>
    );
};

// ==================== MAIN LAYOUT ====================
export const Layout = ({ children }) => {
    const { user, empresa, logout, refreshUser, switchEmpresa } = useAuth();
    const { loadTema, updateManifest, isDark } = useTheme();
    const location = useLocation();
    const navigate = useNavigate();
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [mobileOpen, setMobileOpen] = useState(false);
    const [showChangePassword, setShowChangePassword] = useState(false);
    const [empresaModulos, setEmpresaModulos] = useState([]);
    const [expandedModules, setExpandedModules] = useState({});
    const [allEmpresas, setAllEmpresas] = useState([]);

    useEffect(() => {
        if (empresa?.id) {
            loadTema(empresa.id);
            updateManifest(empresa.subdominio);
        }
    }, [empresa?.id, loadTema, updateManifest]);

    useEffect(() => {
        loadNotifications();
        const interval = setInterval(loadNotifications, 30000);
        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        loadModules();
    }, [user, empresa]);

    useEffect(() => {
        if (user?.rol === 'superadmin') {
            empresaAPI.getAll()
                .then(res => setAllEmpresas(Array.isArray(res.data) ? res.data : []))
                .catch(() => {});
        }
    }, [user?.rol]);

    const loadModules = async () => {
        try {
            const res = await moduloAPI.getEmpresa();
            setEmpresaModulos(res.data);
        } catch { /* ignore */ }
    };

    const loadNotifications = async () => {
        try {
            const response = await notificacionAPI.getAll();
            setNotifications(response.data);
            setUnreadCount(response.data.filter(n => !n.leida).length);
        } catch { /* ignore */ }
    };

    const handleMarkAllRead = async () => {
        try {
            await notificacionAPI.markAllRead();
            loadNotifications();
        } catch { /* ignore */ }
    };

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    const goHome = () => navigate('/home');

    // Build available modules list
    const availableModules = useMemo(() => {
        const userAllowed = user?.modulos_acceso?.length > 0 ? user.modulos_acceso : null;
        return empresaModulos.filter(m => {
            if (userAllowed && !userAllowed.includes(m.codigo)) return false;
            return !!moduleNavConfig[m.codigo]; // Only modules with nav config
        });
    }, [empresaModulos, user]);

    // Build nav items for each module
    const moduleItems = useMemo(() => {
        const rol = user?.rol || 'solicitante';
        const result = {};
        availableModules.forEach(m => {
            const config = moduleNavConfig[m.codigo];
            if (config) {
                result[m.codigo] = config[rol] || config['admin'] || [];
            }
        });
        return result;
    }, [availableModules, user]);

    // Admin-only nav items
    const adminItems = useMemo(() => {
        const items = [];
        if (user?.rol === 'admin' || user?.rol === 'superadmin') {
            items.push({ href: '/gestion-usuarios', label: 'Gestión Usuarios', icon: Users });
        }
        if (user?.rol === 'superadmin') {
            items.push({ href: '/superadmin', label: 'Panel SuperAdmin', icon: Shield });
            items.push({ href: '/admin/modulos', label: 'Módulos', icon: Blocks });
            items.push({ href: '/admin/empresas', label: 'Empresas', icon: Building2 });
        }
        return items;
    }, [user]);

    const toggleModule = (codigo) => {
        setExpandedModules(prev => ({ ...prev, [codigo]: !prev[codigo] }));
    };

    const handleSetPrincipal = async (codigo) => {
        try {
            await authAPI.setModuloPrincipal(codigo);
            await refreshUser();
            toast.success('Módulo principal actualizado');
        } catch {
            toast.error('Error al actualizar módulo principal');
        }
    };

    const empresaNombre = empresa?.nombre_corto || empresa?.nombre || '';
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    const logoField = isDark ? 'logo_url_dark' : 'logo_url_light';
    const rawLogoUrl = empresa?.[logoField] || empresa?.logo_url_light;
    const logoUrl = rawLogoUrl ? `${backendUrl}${rawLogoUrl}` : null;
    const brandConfig = empresa?.tema || {};

    const renderModuleNav = (onNavClick) => (
        <>
            {availableModules.map((modulo) => {
                const items = moduleItems[modulo.codigo] || [];
                if (items.length === 0) return null;
                return (
                    <ModuleSection
                        key={modulo.codigo}
                        modulo={modulo}
                        items={items}
                        expanded={!!expandedModules[modulo.codigo]}
                        onToggle={() => toggleModule(modulo.codigo)}
                        isPrincipal={user?.modulo_principal === modulo.codigo}
                        onSetPrincipal={() => handleSetPrincipal(modulo.codigo)}
                        currentPath={location.pathname}
                        onNavClick={onNavClick}
                    />
                );
            })}
            {adminItems.length > 0 && (
                <div className="mt-3 pt-3 border-t border-border/50">
                    <p className="px-3 mb-1 text-[10px] font-bold uppercase tracking-widest text-muted-foreground/60">
                        Administración
                    </p>
                    {adminItems.map((item) => (
                        <NavLink
                            key={item.href}
                            item={item}
                            isActive={location.pathname === item.href}
                            onClick={onNavClick}
                        />
                    ))}
                </div>
            )}
        </>
    );

    const renderUserBlock = (onLogout, extraClass = '') => (
        <div className={cn('p-4 border-t-2 border-border', extraClass)}>
            <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-sm bg-primary/10 flex items-center justify-center">
                    <User className="h-5 w-5 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">{user?.nombre}</p>
                    <p className="text-xs text-muted-foreground uppercase">{getRolLabel(user?.rol)}</p>
                </div>
            </div>
            <Button
                variant="outline"
                className="w-full justify-start gap-2 text-destructive border-destructive/30 hover:bg-destructive/10 hover:text-destructive"
                onClick={onLogout}
                data-testid="logout-btn"
            >
                <LogOut className="h-4 w-4" />
                Cerrar Sesión
            </Button>
        </div>
    );

    return (
        <div className="min-h-screen bg-background">
            {/* Mobile Header */}
            <header className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-card border-b-2 border-border h-16">
                <div className="flex items-center justify-between h-full px-4">
                    <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
                        <SheetTrigger asChild>
                            <Button variant="ghost" size="icon" data-testid="mobile-menu-btn">
                                <Menu className="h-6 w-6" />
                            </Button>
                        </SheetTrigger>
                        <SheetContent side="left" className="w-72 p-0">
                            <div className="flex flex-col h-full">
                                <div className="border-b-2 border-border" style={{ padding: `${brandConfig?.brand_area_padding ?? 12}px` }}>
                                    <BrandBlock empresaNombre={empresaNombre} logoUrl={logoUrl} onClick={() => { setMobileOpen(false); goHome(); }} brandConfig={brandConfig} />
                                </div>
                                <ScrollArea className="flex-1 p-2">
                                    {renderModuleNav(() => setMobileOpen(false))}
                                </ScrollArea>
                                {renderUserBlock(() => { setMobileOpen(false); handleLogout(); })}
                            </div>
                        </SheetContent>
                    </Sheet>
                    <h1 className="font-heading font-black text-lg uppercase tracking-tight text-primary cursor-pointer" onClick={goHome}>
                        PROT. INDUSTRIAL
                    </h1>
                    <div className="flex items-center gap-1">
                        <ThemeToggle data-testid="theme-toggle-mobile" />
                    </div>
                </div>
            </header>

            {/* Desktop Sidebar */}
            <aside className="hidden lg:flex fixed left-0 top-0 bottom-0 w-64 bg-card border-r-2 border-border flex-col z-40">
                <div className="border-b-2 border-border" style={{ padding: `${brandConfig?.brand_area_padding ?? 12}px` }}>
                    <BrandBlock empresaNombre={empresaNombre} logoUrl={logoUrl} onClick={goHome} size="lg" brandConfig={brandConfig} />
                </div>
                <ScrollArea className="flex-1 p-3">
                    {renderModuleNav()}
                </ScrollArea>
                {renderUserBlock(handleLogout)}
            </aside>

            {/* Main Content Area */}
            <div className="lg:ml-64">
                {/* Desktop Top Bar */}
                <header className="hidden lg:flex fixed top-0 right-0 left-64 z-30 h-16 bg-card/80 backdrop-blur-sm border-b-2 border-border items-center justify-between px-6">
                    <div className="flex items-center gap-2">
                        {user?.rol === 'superadmin' && allEmpresas.length > 1 ? (
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button variant="ghost" className="gap-2 text-muted-foreground hover:text-foreground" data-testid="empresa-selector-btn">
                                        <Building2 className="h-5 w-5" />
                                        <span className="text-sm max-w-[200px] truncate">{empresa?.nombre || 'Seleccionar empresa'}</span>
                                        <ChevronDown className="h-4 w-4 shrink-0" />
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="start" className="w-64">
                                    <DropdownMenuLabel>Cambiar Empresa</DropdownMenuLabel>
                                    <DropdownMenuSeparator />
                                    {allEmpresas.map(e => (
                                        <DropdownMenuItem
                                            key={e.id}
                                            onClick={() => switchEmpresa(e.id)}
                                            className={cn('cursor-pointer', empresa?.id === e.id && 'bg-primary/10 text-primary font-medium')}
                                            data-testid={`switch-empresa-${e.id}`}
                                        >
                                            <Building2 className="h-4 w-4 mr-2 shrink-0" />
                                            <span className="truncate">{e.nombre}</span>
                                            {empresa?.id === e.id && <Badge variant="outline" className="ml-auto text-[10px]">Actual</Badge>}
                                        </DropdownMenuItem>
                                    ))}
                                </DropdownMenuContent>
                            </DropdownMenu>
                        ) : (
                            <>
                                <Building2 className="h-5 w-5 text-muted-foreground" />
                                <span className="text-sm text-muted-foreground">
                                    {empresa?.nombre || (user?.rol === 'superadmin' ? 'Super Administrador' : 'Sin empresa')}
                                </span>
                            </>
                        )}
                    </div>
                    <div className="flex items-center gap-3">
                        <ThemeToggle />

                        {/* Notifications */}
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon" className="relative" data-testid="notifications-btn">
                                    <Bell className="h-5 w-5" />
                                    {unreadCount > 0 && <span className="notification-badge">{unreadCount}</span>}
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-80">
                                <DropdownMenuLabel className="flex items-center justify-between">
                                    <span>Notificaciones</span>
                                    {unreadCount > 0 && (
                                        <Button variant="ghost" size="sm" onClick={handleMarkAllRead} className="text-xs">
                                            Marcar todas leídas
                                        </Button>
                                    )}
                                </DropdownMenuLabel>
                                <DropdownMenuSeparator />
                                <ScrollArea className="h-64">
                                    {notifications.length === 0 ? (
                                        <div className="p-4 text-center text-muted-foreground text-sm">Sin notificaciones</div>
                                    ) : (
                                        notifications.slice(0, 10).map((notif) => (
                                            <DropdownMenuItem
                                                key={notif.id}
                                                className={cn('flex flex-col items-start p-3 cursor-pointer', !notif.leida && 'bg-primary/5')}
                                            >
                                                <span className="font-medium text-sm">{notif.titulo}</span>
                                                <span className="text-xs text-muted-foreground line-clamp-2 mt-1">{notif.mensaje}</span>
                                            </DropdownMenuItem>
                                        ))
                                    )}
                                </ScrollArea>
                            </DropdownMenuContent>
                        </DropdownMenu>

                        {/* User Menu */}
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button variant="ghost" className="gap-2" data-testid="user-menu-btn">
                                    <User className="h-5 w-5" />
                                    <span className="hidden md:inline">{user?.nombre}</span>
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-56">
                                <DropdownMenuLabel>
                                    <div>
                                        <p className="font-medium">{user?.nombre}</p>
                                        <p className="text-xs text-muted-foreground">{user?.email}</p>
                                    </div>
                                </DropdownMenuLabel>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem>
                                    <Badge variant="outline" className="uppercase text-xs">{getRolLabel(user?.rol)}</Badge>
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={() => setShowChangePassword(true)} className="cursor-pointer" data-testid="change-password-menu-item">
                                    <KeyRound className="h-4 w-4 mr-2" />
                                    Cambiar Contraseña
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={handleLogout} className="text-destructive cursor-pointer focus:text-destructive focus:bg-destructive/10" data-testid="logout-menu-item">
                                    <LogOut className="h-4 w-4 mr-2" />
                                    Cerrar Sesión
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>
                </header>

                {/* Page Content */}
                <main className="pt-16 lg:pt-16 min-h-screen">
                    <div className="p-4 md:p-6 lg:p-8">{children}</div>
                </main>
                <GuiasModuleAddons />
                <ChangePasswordModal open={showChangePassword} onClose={() => setShowChangePassword(false)} />
            </div>
        </div>
    );
};

const GuiasModuleAddons = () => {
    const location = useLocation();
    const isGuias = location.pathname.startsWith('/guias');
    useGuiasPushSubscription({ enabled: isGuias });
    if (!isGuias) return null;
    return <PWAInstallBanner />;
};

export default Layout;
